import { Box, Grid, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../../api/hooks/useSmartAndRegularAssets";
import { assetKeys, entityKeys } from "../../../../../api/queryKeys";
import ConfirmationDialog from "../../../../../components/dialogs/ConfirmationDialog";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";

import SvgToIcon from "../../../../../icons/SvgToIcon";
import {
  PLATFORM_USER_ACTIONS,
  QueryParam,
} from "../../../../../utils/constants";
import { icons } from "../../icons/iconExport";
import AddToAssetButton from "../AddToAssetButton";
import AssetChip from "./AssetChip";

const CriticalAssetsListSection = ({ selectedEntity }) => {
  const [
    openDialogForRemoveEntityFromAsset,
    setOpenDialogForRemoveEntityFromAsset,
  ] = useState(false);
  const assetToDelete = useRef(null);
  const { refreshEntitiesOverview, removeEntityFromAsset, trackEvent } =
    useAPI();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const colors = useColors();
  const getAssetsContainingTheEntity = (assets) =>
    assets &&
    assets.filter((asset) =>
      asset?.entity_ids.includes(selectedEntity?.entity_id)
    );

  const { regularAndActiveSmartAsset: assets, isLoadingAll: areAssetsLoading } =
    useSmartAndRegularAssets(false);

  const assetsContainingTheEntity = getAssetsContainingTheEntity(assets);

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_ASSET
  );

  const { mutate: updateTheAssetsContainingTheEntity, isPending } = useMutation(
    {
      mutationFn: async () => {
        const response = await removeEntityFromAsset(
          selectedEntity.entity_id,
          assetToDelete.current.asset_id
        );
        return response;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: assetKeys.all,
        });
        assetToDelete.current = null;
        setOpenDialogForRemoveEntityFromAsset(false);
        Toast.success("Successfully updated critical assets for entity");
        refreshEntitiesOverview().then(() => {
          queryClient.invalidateQueries({
            queryKey: entityKeys.entitiesOverview(),
          });
        });
      },
      onError: () => {
        Toast.error("Error updating the assets containing this entity");
      },
    }
  );

  const handleDelete = () => {
    trackEvent(
      `User removed entity ${selectedEntity.entity_id} from an asset ${assetToDelete.current?.name}`
    ).then();
    updateTheAssetsContainingTheEntity();
  };

  const handleDialogClose = () => {
    setOpenDialogForRemoveEntityFromAsset(false);
    assetToDelete.current = null;
  };

  const handleRemoveEntityFromAsset = (asset) => {
    assetToDelete.current = asset;
    setOpenDialogForRemoveEntityFromAsset(true);
  };

  const getAssetPageUrl = (asset) => {
    return `/inventory/assets?${QueryParam.id}=${asset.asset_id}`;
  };

  return areAssetsLoading ? (
    <LoadingIcon size={25} />
  ) : (
    <Grid
      item
      xs={12}
      display={"flex"}
      flexDirection={"column"}
      gap={"8px"}
      mt={"8px"}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        gap={"10px"}
        alignItems={"center"}
      >
        <Typography variant="subtitle1">Critical Asset</Typography>
      </Box>
      {
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={1}
            flexWrap={"wrap"}
            alignItems={"center"}
          >
            {assetsContainingTheEntity?.map((asset) => (
              <AssetChip
                key={asset.asset_id}
                asset={asset}
                isPending={isPending}
                handleClick={() => navigate(getAssetPageUrl(asset))}
                handleDelete={() => {
                  handleRemoveEntityFromAsset(asset);
                }}
              />
            ))}
            <AddToAssetButton
              entity={selectedEntity}
              diamondStyle={{ cursor: "pointer" }}
              tooltipTitle={
                assetsContainingTheEntity?.length > 0
                  ? "Add to Critical Asset"
                  : null
              }
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                padding: "2px 4px",
                borderRadius: "2px",
                textTransform: "none",
                whiteSpace: "nowrap",
                color: colors.text_secondary,
              }}
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={1}
                alignItems={"center"}
                sx={{
                  color: hasPermission
                    ? colors.text_secondary
                    : colors.text_disabled,
                  cursor: hasPermission ? "pointer" : "default",
                }}
              >
                <SvgToIcon
                  IconLogo={icons.AddIcon}
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                />
                {assetsContainingTheEntity?.length === 0 && (
                  <>
                    <Typography variant="caption">
                      {"Add to Critical Asset"}
                    </Typography>
                  </>
                )}
              </Box>
            </AddToAssetButton>
          </Box>
          <ConfirmationDialog
            openDialog={openDialogForRemoveEntityFromAsset}
            handleDialogClose={handleDialogClose}
            handleConfirm={handleDelete}
            dialogContentFirstLine={`Are you sure you want to remove this entity from critical asset ${assetToDelete.current?.name}?`}
            confirmationButtonText="Remove"
            destructive={true}
          />
        </Box>
      }
    </Grid>
  );
};

export default CriticalAssetsListSection;
